import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase/config';
import { addDoc, collection, serverTimestamp, doc, updateDoc, getDoc } from 'firebase/firestore';
import { processMentions } from '../firebase/helpers';
import { Send } from 'lucide-react';
import { toast } from 'react-hot-toast';

const CommentForm = ({ postId, parentCommentId = null, onCommentPosted }) => {
    const { currentUser, userProfile } = useAuth();
    const [text, setText] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!text.trim() || !currentUser) return;

        const commentText = text.trim();
        setText('');
        setIsSubmitting(true);

        const senderName = userProfile?.displayName || currentUser.displayName || currentUser.email?.split('@')[0] || 'User';
        const senderPhoto = userProfile?.photoURL || currentUser.photoURL || `https://api.dicebear.com/7.x/bottts/svg?seed=${currentUser.uid}`;
        const senderUsername = userProfile?.username || (currentUser.email?.split('@')[0] || 'user').toLowerCase().replace(/[^a-z0-9_]/g, '');

        try {
            // 1. Create Comment Doc
            await addDoc(collection(db, "comments"), {
                text: commentText,
                postId,
                parentCommentId: parentCommentId || null,
                authorId: currentUser.uid,
                authorProfile: {
                    name: senderName,
                    photoURL: senderPhoto,
                    username: senderUsername
                },
                createdAt: serverTimestamp()
            });

            toast.success("Comment posted! 💬");

            // 2. Increment commentCount on post
            try {
                const postRef = doc(db, "posts", postId);
                const postSnap = await getDoc(postRef);
                if (postSnap.exists()) {
                    const postData = postSnap.data() || {};
                    if (!parentCommentId) {
                        const currentCount = postData.commentCount || 0;
                        await updateDoc(postRef, { commentCount: currentCount + 1 }).catch(() => {});
                    }

                    // 3. Mentions
                    processMentions(
                        commentText,
                        currentUser.uid,
                        senderName,
                        postId,
                        (postData.text || '').substring(0, 50),
                        'comment'
                    ).catch(() => {});

                    // 4. Notification for post author
                    const authorId = postData.authorId || postData.userId;
                    if (authorId && authorId !== currentUser.uid) {
                        const notificationRef = collection(db, 'users', authorId, 'notifications');
                        addDoc(notificationRef, {
                            type: 'comment',
                            fromUserId: currentUser.uid,
                            fromUserName: senderName,
                            fromUserAvatar: userProfile?.photoURL || '',
                            postId: postId,
                            postTextSnippet: (postData.text || '').substring(0, 50),
                            commentText: commentText,
                            createdAt: serverTimestamp(),
                            read: false
                        }).catch(() => {});
                    }
                }
            } catch (postUpdateErr) {
                console.warn("Could not update post comment count or send notification:", postUpdateErr);
            }

        } catch (error) {
            console.error("Error adding comment:", error);
            toast.error("Failed to post comment. Please try again.");
            setText(commentText);
        } finally {
            setIsSubmitting(false);
            if (onCommentPosted) onCommentPosted();
        }
    };

    return (
        <form onSubmit={handleSubmit} className="relative z-10">
            <div className="flex items-center gap-2 bg-slate-100 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700/80 p-1.5 rounded-2xl shadow-xs focus-within:ring-2 focus-within:ring-sky-500/30 focus-within:border-sky-500 transition-all">
                <input
                    type="text"
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder={parentCommentId ? "Write a reply..." : "Add to the conversation..."}
                    className="w-full bg-transparent focus:outline-none px-3 py-1.5 text-xs sm:text-sm text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500"
                />
                <button
                    type="submit"
                    disabled={isSubmitting || !text.trim()}
                    className="p-2 rounded-xl bg-sky-500 hover:bg-sky-600 text-white shadow-xs hover:shadow-sky-500/20 active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed transition-all flex-shrink-0"
                >
                    <Send className="w-3.5 h-3.5" />
                </button>
            </div>
        </form>
    );
};

export default CommentForm;
